import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { PushToggle } from "@/components/PushToggle";
import {
  LogOut, LayoutDashboard, Grid3x3, UtensilsCrossed, Users, BarChart3, Settings as Cog,
  Package, ChefHat, Sparkles, Bike,
} from "lucide-react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/tables", label: "Mesas", icon: Grid3x3 },
  { to: "/admin/menu", label: "Menú", icon: UtensilsCrossed },
  { to: "/admin/inventory", label: "Inventario", icon: Package },
  { to: "/admin/recipes", label: "Recetas", icon: ChefHat },
  { to: "/admin/inventory-ai", label: "Predicción", icon: Sparkles },
  { to: "/admin/delivery", label: "Delivery", icon: Bike },
  { to: "/admin/waiters", label: "Meseros", icon: Users },
  { to: "/admin/reports", label: "Reportes", icon: BarChart3 },
  { to: "/admin/settings", label: "Ajustes", icon: Cog },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const expired = user?.subscription_active === false;

  return (
    <div
      className="min-h-screen bg-[#fcfbfd] has-bottom-nav"
      data-testid="admin-layout"
    >
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#fcfbfd]/80 border-b border-[#b182ec]/30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <div className="flex items-center gap-3">
            <Logo size={40} variant="plain" />
            <div>
              <p className="font-display text-lg font-bold text-[#1a0f2e] leading-none">oRdeN</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7248ae]">Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.subscription_active ? (
              <Badge className="bg-[#10b981] hover:bg-[#10b981]/80 hidden sm:inline-flex" data-testid="subscription-active-badge">
                {user.subscription_days_left} días restantes
              </Badge>
            ) : (
              <Badge className="bg-[#e11d48] hover:bg-[#e11d48]/80" data-testid="subscription-expired-badge">Suscripción vencida</Badge>
            )}
            <span className="hidden lg:block text-sm text-[#7248ae]">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-[#3e166c] hover:bg-[#3e166c]/10" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      {expired && (
        <div className="bg-[#e11d48]/10 border-y border-[#e11d48]/30 px-4 py-3 text-center text-sm text-[#e11d48]" data-testid="expired-banner">
          Tu suscripción venció. Puedes consultar la información pero no realizar cambios. Contacta al Owner.
        </div>
      )}

      {/* Desktop side nav */}
      <div className="hidden md:flex max-w-7xl mx-auto px-4 sm:px-8 gap-2 pt-6 flex-wrap" data-testid="admin-nav-desktop">
        {nav.map(n => {
          const Icon = n.icon;
          const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${active ? "bg-[#3e166c] text-white" : "text-[#3e166c] hover:bg-[#3e166c]/10"}`}>
              <Icon className="h-4 w-4" />{n.label}
            </Link>
          );
        })}
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#b182ec]/30"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 30,
          paddingBottom: "env(safe-area-inset-bottom)",
          transform: "translateZ(0)",
          WebkitTransform: "translateZ(0)",
        }}
      >
        <div className="flex overflow-x-auto px-2 py-2 gap-1" data-testid="admin-nav-mobile">
          {nav.map(n => {
            const Icon = n.icon;
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to}
                className={`flex flex-col items-center gap-1 py-1 px-2 rounded-lg flex-shrink-0 min-w-[68px] ${active ? "text-[#3e166c] bg-[#3e166c]/10" : "text-[#7248ae]"}`}>
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium whitespace-nowrap">{n.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
